import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, XCircle, Loader2, AppWindow } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Iter 101 Phase 2 · Session 2 — OIDC consent screen.
// Rendered when `/api/oauth/authorize` decides that the requesting
// client is NOT a trusted first-party app AND the user hasn't already
// granted the requested scopes. The backend 302s the browser here with
// every param preserved; we present a clear "App X wants to..." UI and
// POST the decision back to `/api/oauth/authorize/consent`.

const SCOPE_LABELS = {
  openid:      { label: "Verify your identity", copy: "Confirm who you are (sub identifier only)." },
  profile:     { label: "Basic profile",         copy: "Your name, avatar, and preferred username." },
  email:       { label: "Email address",          copy: "Your work email and verified flag." },
  groups:      { label: "Team & group",           copy: "Which team/group you belong to inside Conduit." },
  partner_org: { label: "Partner organization",   copy: "Your partner org id, name, and type (Reseller / Agent / …)." },
};

export default function OAuthConsent() {
  const { user, loading } = useAuth();
  const [search] = useSearchParams();
  const [busy, setBusy] = useState(false);

  const params = useMemo(() => ({
    client_id:      search.get("client_id") || "",
    client_name:    search.get("client_name") || "an external app",
    redirect_uri:   search.get("redirect_uri") || "",
    scope:          search.get("scope") || "openid",
    state:          search.get("state") || "",
    nonce:          search.get("nonce") || "",
    code_challenge: search.get("code_challenge") || "",
    requested:      (search.get("requested_scopes") || search.get("scope") || "openid").split(/\s+/).filter(Boolean),
    granted:        (search.get("previously_granted") || "").split(/\s+/).filter(Boolean),
  }), [search]);

  if (loading) {
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (!user) {
    // Consent requires a live session; if we ended up here without one
    // (rare race), bounce through /login with the current URL as next.
    const here = window.location.pathname + window.location.search;
    window.location.assign(`/login?next=${encodeURIComponent(here)}`);
    return null;
  }
  if (!params.client_id || !params.redirect_uri || !params.code_challenge) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-900" data-testid="oauth-consent-invalid">
        <XCircle className="mx-auto h-8 w-8" />
        <div className="mt-2 font-semibold">Missing consent parameters</div>
        <p className="mt-1 text-sm">This page must be reached from a valid /api/oauth/authorize redirect. Close this tab and try the sign-in again.</p>
      </div>
    );
  }

  const decide = async (approve) => {
    setBusy(true);
    try {
      const { data } = await api.post("/oauth/authorize/consent", {
        client_id:      params.client_id,
        redirect_uri:   params.redirect_uri,
        scope:          params.scope,
        state:          params.state,
        nonce:          params.nonce,
        code_challenge: params.code_challenge,
        approve,
      });
      // `data.redirect` is the URL the browser should follow next —
      // approved: <redirect_uri>?code=...&state=..., denied:
      // <redirect_uri>?error=access_denied&state=...
      if (data?.redirect) {
        window.location.assign(data.redirect);
      } else {
        toast.error("Backend returned no redirect URL — nothing to follow.");
        setBusy(false);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Consent submission failed");
      setBusy(false);
    }
  };

  const grantedSet = new Set(params.granted);
  const newScopes = params.requested.filter((s) => !grantedSet.has(s));

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-8 shadow-lg" data-testid="oauth-consent-screen">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <AppWindow className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-lg font-bold text-slate-900">
              <span data-testid="consent-client-name">{params.client_name}</span> wants to sign you in
            </h1>
            <p className="text-xs text-slate-500">via your Conduit account · <span className="font-medium text-slate-700">{user.email}</span></p>
          </div>
        </div>

        <div className="mt-5">
          <p className="text-sm text-slate-700">
            Signing in will share the information below with <span className="font-semibold">{params.client_name}</span>. You can revoke this access at any time from <span className="font-medium">Settings → Integrations → OAuth2 Clients</span>.
          </p>

          <ul className="mt-4 space-y-2" data-testid="consent-scope-list">
            {params.requested.map((s) => {
              const meta = SCOPE_LABELS[s] || { label: s, copy: "" };
              const alreadyGranted = grantedSet.has(s);
              return (
                <li key={s}
                    data-testid={`consent-scope-${s}`}
                    className={`rounded-lg border px-3 py-2 ${alreadyGranted ? "border-slate-200 bg-slate-50" : "border-emerald-200 bg-emerald-50"}`}>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className={`h-4 w-4 ${alreadyGranted ? "text-slate-500" : "text-emerald-600"}`} />
                    <div className="text-sm font-semibold text-slate-800">{meta.label}</div>
                    {alreadyGranted && <span className="ml-auto rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">Already granted</span>}
                  </div>
                  {meta.copy && <p className="mt-0.5 pl-6 text-xs text-slate-500">{meta.copy}</p>}
                </li>
              );
            })}
          </ul>

          {newScopes.length > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              <span className="font-semibold text-emerald-700">{newScopes.length}</span> new permission{newScopes.length === 1 ? "" : "s"} will be granted. This decision is remembered for future sign-ins.
            </p>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" disabled={busy} onClick={() => decide(false)} data-testid="consent-deny">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Cancel
          </Button>
          <Button disabled={busy} onClick={() => decide(true)} data-testid="consent-approve" className="bg-emerald-600 text-white hover:bg-emerald-700">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Continue to {params.client_name}
          </Button>
        </div>

        <p className="mt-4 border-t border-slate-100 pt-3 text-[11px] leading-relaxed text-slate-400">
          If you don&apos;t recognize <b>{params.client_name}</b> or didn&apos;t initiate this sign-in, click Cancel and report it to your platform admin. The requesting app cannot access your password or any data outside the permissions shown above.
        </p>
      </div>
    </div>
  );
}
